import {Locale} from '@/utils/constants'
import {IndustryName, IndustryTopicName} from '@/utils/contentful'
import {Jurisdiction, NotOfferedJurisdiction} from '@/utils/countries/constants'
import {routeTo} from '@/utils/router'

export enum RecentArticleKind {
  article = 'article',
  countryGuide = 'countryGuide',
}

export type RecentArticleLink = {
  kind: RecentArticleKind.article
  slug: string
  industry?: IndustryName
  topic?: IndustryTopicName
}

export type RecentCountryGuideLink = {
  kind: RecentArticleKind.countryGuide
  countryCode: Jurisdiction | NotOfferedJurisdiction
}

// A recommendation card: either a real blog post (resolved from Contentful by slug) or a
// country guide page (resolved by country code via getCountrySpecificGuide, with a manually
// built title).
export type RecentArticle = RecentArticleLink | RecentCountryGuideLink

// Adapts a plain slug/industry article reference (e.g. a content config's `articles` field, or a
// question/case/industry entity's related-articles list) into RecentArticleLink entries.
export const toRecentArticleLinks = (
  articles: {slug: string; industry?: IndustryName}[],
): RecentArticle[] =>
  articles.map(({slug, industry}) => ({kind: RecentArticleKind.article, slug, industry}))

export type RecentArticleByLanguage = Record<Locale, RecentArticle[]>

const enRecentArticles: RecentArticle[] = [
  {
    kind: RecentArticleKind.article,
    slug: 'ukipo-fee-increase-how-to-prepare-your-uk-trademark-in-2026',
  },
  {
    kind: RecentArticleKind.article,
    slug: 'stories-of-our-clients-nockta',
    topic: 'stories-of-our-clients',
  },
  {
    kind: RecentArticleKind.article,
    slug: 'trademark-clearance-searches-the-crucial-first-step-in-brand-protection',
    industry: 'legal',
  },
]

export const recentArticles: RecentArticleByLanguage = {
  [Locale.en]: enRecentArticles,
  [Locale.es]: [
    {
      kind: RecentArticleKind.article,
      slug: 'historias-de-nuestros-clientes-nockta',
      topic: 'stories-of-our-clients',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'busquedas-de-disponibilidad-de-marca-el-paso-no-1-en-la-proteccion-de-tu',
      industry: 'legal',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'como-maximizar-las-posibilidades-de-exito-en-el-registro-de-una-marca',
      industry: 'legal',
    },
  ],
  [Locale.sk]: [
    {
      kind: RecentArticleKind.article,
      slug: 'pribehy-nasich-klientov-nockta',
      topic: 'stories-of-our-clients',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'posudenie-registrovatelnosti-klucovy-prvy-krok-k-ochrane-znacky',
      industry: 'legal',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'euipo-grant-2025-pre-male-a-stredne-velke-podniky',
      industry: 'startups',
    },
  ],
  [Locale.cs]: [
    {
      kind: RecentArticleKind.article,
      slug: 'pribehy-nasich-klientu-nockta',
      topic: 'stories-of-our-clients',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'posouzeni-registrovatelnosti-klicovy-prvni-krok-k-ochrane-znacky',
      industry: 'legal',
    },
    {
      kind: RecentArticleKind.article,
      slug: 'euipo-grant-2025-pro-male-a-stredne-velke-podniky',
      industry: 'startups',
    },
  ],
  [Locale.de]: enRecentArticles,
  [Locale.fr]: enRecentArticles,
  [Locale.it]: enRecentArticles,
  [Locale.pl]: enRecentArticles,
  [Locale.pt]: enRecentArticles,
}

export const useRecentArticlesContent = () => ({
  title: 'recent-blogs-title',
  buttonLinkText: 'button-see-all-articles',
  buttonLinkUrl: routeTo.blog.home(),
})
